import classNames from "classnames";
import styles from "./brands-list-images.module.scss";
import { Url } from "@/helpers/url";
import { B2cBrand, BusinessBrand } from "@/generated/graphql/b2c";
import "@/style/fonts.module.scss";
import { LayoutStyleTypes } from "@/util-react/layout";
import { getIconUrl } from "@/util/menu.ts";
import {useCallback, useEffect, useRef, useState} from "preact/hooks";
import arrowRightIcon from "@/assets/arrow-right.svg";
import {Icon} from "@/elements-react/fix-icon";
import { BrandLogoEnabled } from "@/util/constants";
import React from "preact/compat";

export type BrandsListImagesProps = {
  type: LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_LG;
  brands: B2cBrand[];
  className?: string;
};

export type BrandType = { id: string; url: string; name: string; hasLogo: boolean; logoUrl: string | null };

export type BrandsImageProps = {
    brand: BrandType;
    className?: string;
    showNameUnderLogo?: boolean;
    nameClassName?: string;
    showLogo?: boolean;
};

export const getList = (brands: B2cBrand[] | BusinessBrand[]) =>
  brands.map((item) => {
    const assetLogo = Array.isArray(item.assets)
      ? item.assets?.find((asset) => asset?.type === "image-logo")?.path ?? null
      : null;
    const logoUrl = getIconUrl(assetLogo);
    return {
      id: item.id,
      name: item.name,
      slug: item.slug,
      url: Url.to("dynamic-route", { dynamicPath: [item.slug] }),
      hasLogo: Boolean(logoUrl),
      logoUrl,
    };
  });

export const BrandCard: React.FC<BrandsImageProps> = ({ brand, className = null, showNameUnderLogo = false, nameClassName, showLogo = true })  => (
    <a
        className={classNames(className, styles.item, {
            [styles.withLabel]: showNameUnderLogo,
        })}
        href={brand.url}
        key={brand.id}
    >
        {brand.hasLogo && showLogo ? (
            <span className={styles.iconWrapper}>
              <img
                  className={styles.icon}
                  src={brand.logoUrl || undefined}
                  alt={brand.name}
                  title={brand.name}
              />
            </span>
        ) : (
            <span className={styles['textWrapper']}>
              {brand.name}
            </span>
        )}

    {showNameUnderLogo && brand.hasLogo && <span className={classNames(styles.brandName, nameClassName)}>{brand.name}</span>}
  </a>
);

export const BrandsListImages: React.FC<BrandsListImagesProps> = ({ type, brands, className }) => {
  const list = getList(brands);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    const checkScroll = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;

        const { scrollLeft, scrollWidth, clientWidth } = el;

        setShowLeft(scrollLeft > 0);
        setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);

    useEffect(() => {
        checkScroll();

        const el = containerRef.current;
        if (!el) return;

        el.addEventListener("scroll", checkScroll);
        window.addEventListener("resize", checkScroll);

        return () => {
            el.removeEventListener("scroll", checkScroll);
            window.removeEventListener("resize", checkScroll);
        };
    }, [checkScroll]);

    const scrollByAmount = (direction: "left" | "right") => {
        const el = containerRef.current;
        if (!el) return;

        const amount = el.clientWidth * 0.8;

        el.scrollBy({
            left: direction === "left" ? -amount : amount,
            behavior: "smooth",
        });
    };

    return (
        <div className={classNames(styles.wrapper, className)}>
            {showLeft && (
                <button
                    className={classNames(styles.scrollButton, styles.left)}
                    aria-label="Scroll left"
                    onClick={() => scrollByAmount("left")}
                >
                    <Icon src={arrowRightIcon as string} height={16} width={16} />
                </button>
            )}

            <div ref={containerRef} className={classNames(styles.root, styles[type], "font-lg-normal")}>
                {list.map((item) => (
                    <BrandCard brand={item as BrandType}></BrandCard>
                ))}
            </div>

            {showRight && (
                <button
                    className={classNames(styles.scrollButton, styles.right)}
                    onClick={() => scrollByAmount("right")}
                    aria-label="Scroll right"
                >
                    <Icon src={arrowRightIcon as string} height={16} width={16} />
                </button>
            )}
        </div>
    );
};

export default BrandsListImages;
